import { useCallback, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import {
  DEFAULT_MAX_INPUT_BYTES,
  JobStatus,
  detectInputKind,
  type CreateJobResponseViewModel,
  type JobViewModel,
} from "@models/job";

export interface JobSubmitArgs {
  service: string;
  pluginId: string;
  file: File;
}

// A client-side pre-upload validation failure that maps to a friendly,
// localized message under `mappingServicePage.upload.errors`.
class UploadValidationError extends Error {
  readonly i18nKey: string;

  constructor(i18nKey: string) {
    super(i18nKey);
    this.name = "UploadValidationError";
    this.i18nKey = i18nKey;
  }
}

const ERROR_CODE_TO_I18N_KEY: Readonly<Record<string, string>> = {
  upload_network: "network",
  upload_aborted: "aborted",
  upload_expired: "expired",
  upload_too_large: "tooLarge",
  upload_server_error: "serverError",
  upload_http_error: "httpError",
};

export interface UploadProgressState {
  filename: string;
  percent: number;
}

export interface FailedUploadState {
  filename: string;
  args: JobSubmitArgs;
}

export interface JobSubmitHook {
  submit: (args: JobSubmitArgs) => Promise<JobViewModel | null>;
  retry: () => Promise<JobViewModel | null>;
  isSubmitting: boolean;
  uploadProgress: UploadProgressState | null;
  lastUploadedFilename: string | null;
  clearLastUploaded: () => void;
  errorKey: string | null;
  errorText: string | null;
  failedUpload: FailedUploadState | null;
  clearError: () => void;
  dismissFailedUpload: () => void;
}

export const useJobSubmit = (
  ensureConnected: () => Promise<void>,
  subscribe: (jobId: string) => () => void,
): JobSubmitHook => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressState | null>(null);
  const [lastUploadedFilename, setLastUploadedFilename] = useState<
    string | null
  >(null);
  const [failedUpload, setFailedUpload] = useState<FailedUploadState | null>(
    null,
  );
  const failedUploadRef = useRef<FailedUploadState | null>(null);

  const clearError = useCallback((): void => {
    setErrorKey(null);
    setErrorText(null);
  }, []);

  const clearLastUploaded = useCallback((): void => {
    setLastUploadedFilename(null);
  }, []);

  const dismissFailedUpload = useCallback((): void => {
    failedUploadRef.current = null;
    setFailedUpload(null);
  }, []);

  const submit = useCallback(
    async ({
      service,
      pluginId,
      file,
    }: JobSubmitArgs): Promise<JobViewModel | null> => {
      setIsSubmitting(true);
      setErrorKey(null);
      setErrorText(null);
      try {
        const inputKind = detectInputKind(file.name);
        if (!inputKind) {
          throw new UploadValidationError("unsupportedFormat");
        }
        if (file.size === 0) {
          throw new UploadValidationError("emptyFile");
        }
        if (file.size > DEFAULT_MAX_INPUT_BYTES) {
          throw new UploadValidationError("fileTooLarge");
        }

        const createResponse: CreateJobResponseViewModel =
          await mappingApi.createJob({
            service,
            plugin_id: pluginId,
            input_kind: inputKind,
            filename: file.name,
          });

        if (file.size > createResponse.maxBytes) {
          throw new UploadValidationError("fileTooLarge");
        }

        try {
          await ensureConnected();
          subscribe(createResponse.jobId);
        } catch (wsError) {
          console.warn(
            "WebSocket connect failed; status updates will be limited:",
            wsError,
          );
        }

        setUploadProgress({ filename: file.name, percent: 0 });
        await mappingApi.uploadFileToS3(
          createResponse.uploadUrl,
          file,
          createResponse.uploadContentType,
          (percent) => setUploadProgress({ filename: file.name, percent }),
        );
        setUploadProgress(null);
        setLastUploadedFilename(file.name);
        failedUploadRef.current = null;
        setFailedUpload(null);

        const now = new Date().toISOString();
        return {
          jobId: createResponse.jobId,
          pluginId,
          status: JobStatus.Parsing,
          inputKind: inputKind === "zip" ? "zip" : "single",
          filename: file.name,
          fileCount: inputKind === "zip" ? 0 : 1,
          filesDone: 0,
          failedFiles: 0,
          rowsTotal: null,
          rowsOk: 0,
          rowsFailed: 0,
          rowsNoMatch: 0,
          acceptedCount: 0,
          error: null,
          createdAt: now,
          updatedAt: now,
        };
      } catch (err) {
        if (err instanceof UploadValidationError) {
          setErrorKey(err.i18nKey);
        } else if (
          err instanceof ApiError &&
          err.errorCode &&
          err.errorCode in ERROR_CODE_TO_I18N_KEY
        ) {
          setErrorKey(ERROR_CODE_TO_I18N_KEY[err.errorCode]);
        } else {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "Failed to submit mapping job";
          setErrorText(message);
        }
        setUploadProgress(null);
        const failed: FailedUploadState = {
          filename: file.name,
          args: { service, pluginId, file },
        };
        failedUploadRef.current = failed;
        setFailedUpload(failed);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [ensureConnected, subscribe],
  );

  const retry = useCallback(async (): Promise<JobViewModel | null> => {
    const target = failedUploadRef.current;
    if (!target) return null;
    return submit(target.args);
  }, [submit]);

  return {
    submit,
    retry,
    isSubmitting,
    uploadProgress,
    lastUploadedFilename,
    clearLastUploaded,
    errorKey,
    errorText,
    failedUpload,
    clearError,
    dismissFailedUpload,
  };
};
